export const GMAIL_ACCOUNT = "globemet@gmail.com";
const COMPOSE_SCOPE = "https://www.googleapis.com/auth/gmail.compose";
let loading;

export const prepareGmail = () => {
  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID || window.google?.accounts?.oauth2) return Promise.resolve();
  if (!loading) loading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { script.remove(); loading = null; reject(new Error("Google sign-in could not load. Please reload and try again.")); };
    document.head.appendChild(script);
  });
  return loading;
};

// Called directly from the email button, before any awaits, to allow Google's authorization popup.
export const authorizeGmail = () => new Promise((resolve, reject) => {
  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) return reject(new Error("Automatic attachments need Gmail setup: configure REACT_APP_GOOGLE_CLIENT_ID and rebuild the frontend."));
  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) return reject(new Error("Google sign-in is still loading. Please try again in a moment."));
  const client = oauth.initTokenClient({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: `${COMPOSE_SCOPE} https://www.googleapis.com/auth/userinfo.email`,
    hint: GMAIL_ACCOUNT,
    callback: result => {
      if (result.error || !result.access_token || !oauth.hasGrantedAllScopes(result, COMPOSE_SCOPE, "https://www.googleapis.com/auth/userinfo.email")) {
        reject(new Error("Gmail draft permission was not granted. No email was created."));
      } else resolve(result.access_token);
    },
    error_callback: () => reject(new Error("Google sign-in was closed or blocked. Please click Email again."))
  });
  client.requestAccessToken();
});

const base64Utf8 = value => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};
const wrapBase64 = value => value.match(/.{1,76}/g)?.join("\r\n") || "";
const blobBase64 = blob => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = () => reject(new Error("The attachment could not be read."));
  reader.readAsDataURL(blob);
});

export const buildDraftMime = async ({ subject, body, blob, filename, format }) => {
  const boundary = `fixation_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const safeName = filename.replace(/[^a-zA-Z0-9_.-]/g, "_");
  const mime = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const parts = [
    `From: ${GMAIL_ACCOUNT}`, `Subject: =?UTF-8?B?${base64Utf8(subject)}?=`, "MIME-Version: 1.0",
    `Content-Type: multipart/mixed; boundary="${boundary}"`, "",
    `--${boundary}`, 'Content-Type: text/plain; charset="UTF-8"', 'Content-Transfer-Encoding: base64', "", wrapBase64(base64Utf8(body)),
    `--${boundary}`, `Content-Type: ${mime}; name="${safeName}"`, `Content-Disposition: attachment; filename="${safeName}"`,
    'Content-Transfer-Encoding: base64', "", wrapBase64(await blobBase64(blob)), `--${boundary}--`, ""
  ];
  return base64Utf8(parts.join("\r\n")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const createGmailDraft = async (token, attachment) => {
  const headers = { Authorization: `Bearer ${token}` };
  const profileResponse = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", { headers });
  if (!profileResponse.ok) throw new Error("Gmail authorization expired. Please click Email again.");
  const profile = await profileResponse.json();
  if (profile.email?.toLowerCase() !== GMAIL_ACCOUNT) throw new Error(`Please authorize ${GMAIL_ACCOUNT}. No draft was created in the other account.`);
  const raw = await buildDraftMime(attachment);
  const response = await fetch("https://gmail.googleapis.com/gmail/v1/users/me/drafts", {
    method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify({ message: { raw } })
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error?.message || "Gmail could not create the draft. Please try again.");
  if (!result.id) throw new Error("Gmail did not confirm draft creation.");
  // Gmail's supported API creates the saved draft; open Drafts for the user to review and send.
  return `https://mail.google.com/mail/?authuser=${encodeURIComponent(GMAIL_ACCOUNT)}#drafts`;
};
